/**
 * Tenant slug resolution from the browser URL.
 *
 * The mirror of the backend's `tenancy/host_resolution.py`. Single source
 * of truth for "given the current URL, which tenant are we?" - used by the
 * RTK Query base API (to set the X-Tenant-Slug header) and by the login /
 * forgot-password pages (to send `tenant_slug` in the request body).
 *
 * Resolution priority - highest first:
 *
 *   1. PATH PREFIX  /o/<slug>/...
 *      The single-host deployment path. On tnn.new901.io the hostname
 *      cannot carry a tenant, so the tenant lives in the URL path:
 *      tnn.new901.io/o/memphis-rox/login. This is the demo path today
 *      and a permanent fallback (local dev, pre-DNS tenants).
 *
 *   2. SUBDOMAIN  <slug>.<apex>
 *      The long-term path. memphis-rox.tnn.new901.io → "memphis-rox".
 *      Apex comes from env.apexDomain, which MUST match the backend's
 *      TNN_APEX_DOMAIN.
 *
 * Why path beats subdomain: if someone is deliberately on an /o/<slug>/
 * URL, that's an explicit choice and should win over whatever the host
 * happens to be (e.g. during a migration, or on a shared host).
 *
 * Returns null when no tenant can be determined - that's the correct
 * state for the bare apex, the control plane, reserved subdomains, and
 * local dev without an /o/ prefix (where the store-held slug from login
 * takes over instead).
 */
import { env } from "@/shared/config/env";

/** Subdomains owned by TNN infrastructure - never tenant slugs. */
const RESERVED_SUBDOMAINS = new Set(["www", "admin", "api", "app"]);

/**
 * Extract a tenant slug from a URL path of the form /o/<slug>/...
 * Returns null if the path doesn't start with the /o/ prefix.
 */
export function extractTenantSlugFromPath(pathname: string): string | null {
  const parts = pathname.split("/").filter(Boolean); // drop empty segments
  if (parts.length >= 2 && parts[0] === "o" && parts[1]) {
    return parts[1].toLowerCase();
  }
  return null;
}

/**
 * Extract a tenant slug from a hostname, given the configured apex.
 * Mirrors backend `extract_tenant_slug_from_host`.
 */
export function extractTenantSlugFromHost(
  hostname: string,
  apex: string = env.apexDomain,
): string | null {
  const host = hostname.trim().toLowerCase();
  const apexLower = apex.trim().toLowerCase();

  // localhost / raw IPv4 - no tenant in the host; dev relies on the
  // store-held slug or an /o/ path.
  if (host === "localhost" || /^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;

  // The bare apex is not a tenant.
  if (host === apexLower) return null;

  // Must be a strict subdomain of the apex.
  const suffix = `.${apexLower}`;
  if (!host.endsWith(suffix)) return null;

  const subdomainPortion = host.slice(0, -suffix.length);
  if (!subdomainPortion) return null;
  // Leftmost label is the slug (defined behavior for multi-label hosts).
  const slug = subdomainPortion.split(".")[0];
  if (!slug || RESERVED_SUBDOMAINS.has(slug)) return null;
  return slug;
}

/**
 * Resolve the tenant slug for the current browser location.
 *
 * Path prefix wins over subdomain (see module docstring). Returns null
 * if neither yields a tenant - callers fall back to the store-held slug
 * (set at login) or treat the request as control-plane.
 */
export function resolveTenantSlug(): string | null {
  if (typeof window === "undefined") return null;
  return (
    extractTenantSlugFromPath(window.location.pathname) ??
    extractTenantSlugFromHost(window.location.hostname)
  );
}

/** Slug from VITE_TENANT_SLUG (defaults to tnn) when the URL has no tenant. */
export function resolveConfiguredTenantSlug(): string {
  return env.tenantSlug;
}

/** URL slug, then configured env slug. */
export function resolveEffectiveTenantSlug(): string | null {
  return resolveTenantSlug() ?? resolveConfiguredTenantSlug();
}

/**
 * Tenant slug for pre-auth form bodies (login, forgot-password).
 *
 * Uses manifest or URL path/subdomain only — never VITE_TENANT_SLUG.
 * Sending the env default (e.g. `tnn`) on the bare apex breaks tenant-
 * scoped user lookup when the account belongs to another org.
 */
export function resolveAuthFormTenantSlug(
  manifestTenantSlug?: string | null,
): string | null {
  const fromManifest = manifestTenantSlug?.trim();
  if (fromManifest) return fromManifest.toLowerCase();
  return resolveTenantSlug();
}

/** `tenant_slug` for forgot-password POST body (form field, then URL slug). */
export function resolveForgotPasswordTenantSlug(
  formSlug?: string | null,
  urlSlug?: string | null,
): string | undefined {
  const slug = formSlug?.trim() || urlSlug?.trim() || "";
  return slug || undefined;
}

export interface RequestAuthContext {
  accessToken: string | null;
  user: unknown | null;
  tenantSlug: string | null;
}

export interface ResolveRequestTenantSlugOptions {
  /** Override URL/env resolution (tests). Defaults to resolveEffectiveTenantSlug(). */
  urlSlug?: string | null;
  /** Override VITE_TENANT_SLUG fallback (tests). Defaults to env.tenantSlug. */
  configuredSlug?: string | null;
}

/**
 * Tenant slug for outbound API requests (X-Tenant-Slug header).
 *
 * Priority:
 *   1. URL (/o/<slug>/ or <slug>.<apex> subdomain) - explicit tenant
 *      context from the browser location always wins (e.g. tnn.new901.io/o/memphis-rox/).
 *   2. Slug stored at login - only when accessToken + user are present
 *      and the URL carries no tenant (prevents a stale slug from a previous
 *      session leaking before login - audit V4).
 *   3. VITE_TENANT_SLUG from env.
 */
export function resolveRequestTenantSlug(
  auth: RequestAuthContext,
  options?: ResolveRequestTenantSlugOptions,
): string | null {
  const hasSession = Boolean(auth.accessToken && auth.user);
  const urlSlug =
    options?.urlSlug !== undefined ? options.urlSlug : resolveTenantSlug();
  const configuredSlug =
    options?.configuredSlug !== undefined
      ? options.configuredSlug
      : resolveConfiguredTenantSlug();

  // Normalize each candidate: an empty / whitespace-only string is NOT a
  // valid slug and must be treated as "absent" so the `??` chain falls
  // through. Without this, a blank stored tenantSlug ("") short-circuits
  // the fallback and the X-Tenant-Slug header is dropped entirely - // backend then rejects the request with `tnn.tenancy.slug_missing`.
  const clean = (s: string | null | undefined): string | null => {
    const t = s?.trim();
    return t ? t : null;
  };

  return (
    clean(urlSlug) ??
    (hasSession ? clean(auth.tenantSlug) : null) ??
    clean(configuredSlug)
  );
}

/**
 * The React Router `basename` for the current location.
 *
 * When the app is served under an /o/<slug>/ path prefix (the single-host
 * deployment), every route must be mounted under that prefix so that all
 * existing prefix-free <Route>s, <Link>s, and navigate() calls Just Work
 * without the slug leaking into every path string.
 *
 * Returns "/o/<slug>" when the URL has that prefix, else "/" (the normal
 * root - used for subdomain deployments and local dev).
 *
 * Computed ONCE at app startup. The slug doesn't change within a session
 * without a full page load, so a static basename is correct.
 */
export function getRouterBasename(): string {
  if (typeof window === "undefined") return "/";
  const pathSlug = extractTenantSlugFromPath(window.location.pathname);
  return pathSlug ? `/o/${pathSlug}` : "/";
}

/**
 * Build an absolute app path under the /o/<slug>/ prefix.
 *
 * Example: buildTenantAppPath("memphis-rox", "/login")
 *   → "/o/memphis-rox/login"
 */
export function buildTenantAppPath(slug: string, subpath = "/"): string {
  const normalized = slug.trim().toLowerCase();
  if (!normalized) return subpath.startsWith("/") ? subpath : `/${subpath}`;

  const suffix = subpath === "/" ? "" : subpath.startsWith("/") ? subpath : `/${subpath}`;
  return `/o/${normalized}${suffix}`;
}

/**
 * True when the URL is scoped to a *different* tenant (e.g. /o/other-org/).
 * False when there is no tenant in the URL (localhost, bare /login) - not a conflict.
 */
export function tenantUrlConflictsWithSlug(slug: string | null | undefined): boolean {
  const target = slug?.trim().toLowerCase();
  if (!target) return false;
  const current = resolveTenantSlug()?.toLowerCase();
  if (!current) return false;
  return current !== target;
}

/**
 * True when the user should be sent to `/o/<slug>/` after login (no tenant in URL,
 * or wrong tenant). Used by resolvePostLoginHref, not quick-login pre-redirect.
 */
export function needsTenantPathAfterLogin(slug: string | null | undefined): boolean {
  const target = slug?.trim().toLowerCase();
  if (!target) return false;
  const current = resolveTenantSlug()?.toLowerCase();
  if (!current) return true;
  return current !== target;
}

/** @deprecated Use tenantUrlConflictsWithSlug or needsTenantPathAfterLogin. */
export function tenantUrlDiffersFromSlug(slug: string | null | undefined): boolean {
  return needsTenantPathAfterLogin(slug);
}

/**
 * Apex login URL after sign-out or session loss (no /o/<slug>/ prefix).
 * Use with window.location.assign - SPA navigate() cannot escape router basename.
 */
export const BARE_LOGIN_PATH = "/login";

/** @deprecated Prefer BARE_LOGIN_PATH; kept for callers that pass a slug argument. */
export function resolveLoginPath(_tenantSlug?: string | null): string {
  return BARE_LOGIN_PATH;
}

/**
 * Where to send the user after login.
 *
 * Returns an absolute path for `window.location.assign` when the current
 * URL prefix must change (wrong /o/<slug>/ or control user leaving a
 * tenant path). Returns null when React Router `navigate` is enough.
 */
export function resolvePostLoginHref(options: {
  userType: string;
  tenantSlug: string | null;
  homePath?: string;
}): string | null {
  const currentUrlSlug = resolveTenantSlug();

  if (options.userType === "control") {
    return currentUrlSlug ? "/control" : null;
  }

  const slug = options.tenantSlug?.trim();
  if (!slug) return null;

  if (needsTenantPathAfterLogin(slug)) {
    return buildTenantAppPath(slug, options.homePath ?? "/");
  }
  return null;
}

/** Human-readable tenant portal URL for QA panels (no protocol). */
export function formatTenantPortalHost(
  slug: string,
  apex: string = env.apexDomain,
): string {
  const normalized = slug.trim().toLowerCase();
  return normalized ? `${apex}/o/${normalized}` : apex;
}
